import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { computeStratigraphyCosts } from '@/components/configurator-v2/hooks/computeStratigraphyCosts';
import type { LayerV2 } from '@/components/configurator-v2/types';

/**
 * Ricalcolo LIVE del prezzo di una stratigrafia, allineato al bulk update.
 *
 * - Carica i layer + materiali via FK
 * - Override unit_price coi NETTI da `materials_with_pricing` (sconti famiglia+extra org-aware via RLS)
 * - Costo orario corrente da `configurator_settings`
 * - Chiama `computeStratigraphyCosts` (stessa formula del builder + bulk update)
 *
 * Ritorna il €/m² "parete pura" (escluso finitura). La finitura va sommata
 * dal chiamante (sia AddToEstimateDialog che useCreateEstimateStratigraphy).
 *
 * Storia bug:
 *  - 2026-05-25: introduzione hook per allineare 3 valori divergenti
 *    (dialog €73.77, INSERT €69.73, UPDATE €69.97).
 *    Causa: il CREATE non includeva `width` nel fetch materials → fallback
 *    width=75 anziché reale (es. 50) → cover isolante calcolato male.
 *    Vedi `computeStratigraphyCosts.ts` linea 83.
 */
export function useStratigraphyLivePrice(stratigraphyId: string | null) {
  return useQuery({
    queryKey: ['stratigraphy-live-price', stratigraphyId],
    enabled: !!stratigraphyId,
    staleTime: 30 * 1000, // 30s — refresh frequente per riflettere cambi prezzo
    queryFn: async (): Promise<number | null> => {
      if (!stratigraphyId) return null;

      // Fetch stratigraphy + layers + materials (TUTTI i campi rilevanti per computeStratigraphyCosts)
      const { data: stratigraphy, error: stratError } = await supabase
        .from('stratigraphies')
        .select(`
          id,
          stud_spacing_mm,
          layers (
            id,
            position,
            thickness,
            inter_axis,
            material_id,
            screw_material_id,
            anchor_material_id,
            screw_quantity,
            screw_cost_per_sqm,
            material_quantity_per_sqm,
            materials!layers_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              incidence_per_sqm,
              supplier,
              code,
              width,
              weight_per_sqm,
              installation_time_per_sqm,
              waste_percentage,
              box_pieces,
              length
            ),
            screw_materials:materials!layers_screw_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              supplier,
              code,
              installation_time_per_sqm,
              waste_percentage,
              box_pieces,
              length
            ),
            anchor_materials:materials!layers_anchor_material_id_fkey (
              id,
              name,
              category,
              unit,
              unit_price,
              supplier,
              code,
              installation_time_per_sqm,
              waste_percentage,
              box_pieces,
              length
            )
          )
        `)
        .eq('id', stratigraphyId)
        .maybeSingle();

      if (stratError || !stratigraphy) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dbLayers: any[] = (stratigraphy as any).layers ?? [];
      if (dbLayers.length === 0) return null;

      // Override unit_price coi NETTI da materials_with_pricing
      const matIds: string[] = [];
      for (const l of dbLayers) {
        if (l.materials?.id) matIds.push(l.materials.id);
        if (l.screw_materials?.id) matIds.push(l.screw_materials.id);
        if (l.anchor_materials?.id) matIds.push(l.anchor_materials.id);
      }
      const uniqueMatIds = Array.from(new Set(matIds));
      if (uniqueMatIds.length > 0) {
        const { data: pricing } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('materials_with_pricing' as any)
          .select('id, unit_price')
          .in('id', uniqueMatIds);
        const priceMap = new Map<string, number>();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const p of (pricing ?? []) as any[]) {
          priceMap.set(p.id, Number(p.unit_price ?? 0));
        }
        for (const l of dbLayers) {
          if (l.materials?.id && priceMap.has(l.materials.id)) {
            l.materials.unit_price = priceMap.get(l.materials.id);
          }
          if (l.screw_materials?.id && priceMap.has(l.screw_materials.id)) {
            l.screw_materials.unit_price = priceMap.get(l.screw_materials.id);
          }
          if (l.anchor_materials?.id && priceMap.has(l.anchor_materials.id)) {
            l.anchor_materials.unit_price = priceMap.get(l.anchor_materials.id);
          }
        }
      }

      // Costo orario corrente
      const { data: settingData } = await supabase
        .from('configurator_settings')
        .select('value')
        .eq('key', 'cost_per_hour')
        .maybeSingle();
      const costPerHour = parseFloat(settingData?.value ?? '30') || 30;

      // Costruisci LayerV2[] (stesso shape di bulk update)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sortedLayers = [...dbLayers].sort((a: any, b: any) => a.position - b.position);
      const layersForCompute = sortedLayers.map((l, idx) => ({
        id: l.id ?? `tmp-${idx}`,
        position: idx + 1,
        materialId: l.material_id ?? null,
        material: l.materials ?? null,
        thickness: Number(l.thickness ?? 0),
        interAxis: l.inter_axis ?? undefined,
        screwMaterialId: l.screw_material_id ?? null,
        screwMaterial: l.screw_materials ?? null,
        anchorMaterialId: l.anchor_material_id ?? null,
        anchorMaterial: l.anchor_materials ?? null,
        screwQuantity: l.screw_quantity ?? undefined,
        screwCostPerSqm: l.screw_cost_per_sqm ?? undefined,
        materialQuantity: l.material_quantity_per_sqm ?? null,
      })) as unknown as LayerV2[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const studSpacingMm = Number((stratigraphy as any).stud_spacing_mm ?? 600);
      const breakdown = computeStratigraphyCosts(layersForCompute, studSpacingMm, costPerHour);

      if (breakdown.totalCost <= 0) return null;
      return Math.round(breakdown.totalCost * 100) / 100;
    },
  });
}
